// The inline Assist session card (29 Oct 2026, Julian 28 Oct 21:30): when an agent has a client's
// (or his own) computer open through Assist, the chat shows a card like the Claude desktop app's
// browser preview: the picture on top, the shop's name, ONE small grey line, ONE white "Open"
// button, no menu. The whole card is one link to that session in the Assist page.
//
// Live state only (lib/assistWatch.ts): a session belongs to this chat when the watch service says
// `chat === <this chat's id>`. No session = nothing rendered. Behind the runtime kill switch
// /assist-card.json (lib/assistCardFlag.ts): off = this renders nothing and asks nothing.
// Every title, line and reason is React TEXT, never HTML. A card that crashes falls back to its
// title and a plain Open link (CardBoundary), and a crash of the list itself renders nothing.
import { Component, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { Monitor } from "react-feather";
import { assistCardEnabled } from "../../lib/assistCardFlag";
import { assistWatch, releaseUrl, retainUrl, type CardModel, type Snapshot } from "../../lib/assistWatch";
import "../../styles/assist-card.css";

const NONE: Snapshot = { chatId: null, cards: [] };
/** Assist opens in ONE named tab, so a second tap re-routes it instead of piling up tabs (no noopener: a named
 *  target is only reused when the link is not noopener, and /assist/ is the same origin and our own code). */
const ASSIST_TAB = "reputifly-assist";
/** A card that arrives while the chat rests within this distance of its end brings itself into view. */
const NEAR_BOTTOM_PX = 140;

/** Mounted after the last message of the open chat. Renders nothing unless the switch is on AND a session matches. */
export default function AssistSessionCards({ chatId }: { chatId: string }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let alive = true;
    const check = () => { void assistCardEnabled().then((v) => { if (alive) setOn(v); }); };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    check();
    document.addEventListener("visibilitychange", onVisible);
    // The kill switch must reach a tab that simply stays open (a laptop's Build tab for hours): look again each minute
    // (assistCardEnabled answers from its own 60 s cache, so this is at most one tiny file read a minute).
    const iv = window.setInterval(onVisible, 60_000);
    return () => { alive = false; window.clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); };
  }, []);
  if (!on) return null;
  return <Quiet><LiveCards chatId={chatId} /></Quiet>;
}

function LiveCards({ chatId }: { chatId: string }) {
  const store = assistWatch();
  useEffect(() => store.open(chatId), [store, chatId]);
  const snap = useSyncExternalStore(store.subscribe, store.getSnapshot, () => NONE);
  const wrap = useRef<HTMLDivElement>(null);
  const had = useRef(false);
  const shown = snap.chatId === chatId && snap.cards.length > 0;
  // The card arrives after the chat has loaded and scrolled to its end, so it would sit under the composer: when the chat
  // was resting at (near) the bottom, follow it down. Someone reading older messages is never moved.
  useLayoutEffect(() => {
    if (shown && !had.current) {
      const el = wrap.current, sc = el?.closest<HTMLElement>(".chat__scroll");
      if (el && sc) {
        const before = sc.scrollHeight - sc.scrollTop - sc.clientHeight - el.offsetHeight - 18;
        if (before <= NEAR_BOTTOM_PX) sc.scrollTo({ top: sc.scrollHeight });
      }
    }
    had.current = shown;
  }, [shown]);
  if (!shown) return null;
  return (
    <div className="asCards" data-assist-cards="" ref={wrap}>
      {snap.cards.map((card) => (
        <CardBoundary key={card.key2} href={card.href} title={card.title}>
          <AssistCard card={card} />
        </CardBoundary>
      ))}
    </div>
  );
}

/** Holds the frame's object URL while it is on screen; released (and revoked when nobody holds it) after. */
function useHeldUrl(url: string | null) {
  useEffect(() => {
    if (!url) return;
    retainUrl(url);
    return () => releaseUrl(url);
  }, [url]);
}

export function AssistCard({ card }: { card: CardModel }) {
  useHeldUrl(card.src);
  const lineId = useId();
  return (
    <div className={`asCard is-${card.key}${card.working ? " is-working" : ""}`} data-assist-session={card.id}>
      <div className="asCard__pic" aria-hidden="true">
        {card.src
          ? <img className="asCard__img" src={card.src} alt="" decoding="async" draggable={false} />
          : card.waiting
            ? <span className="asCard__wait" />
            : <Monitor className="asCard__glyph" width={28} height={28} strokeWidth={1.5} />}
      </div>
      <div className="asCard__foot">
        <div className="asCard__text">
          <p className="asCard__title">{card.title}</p>
          <p className="asCard__sub" id={lineId}>
            {card.dot && <span className={`asCard__dot${card.working ? " is-working" : ""}`} aria-hidden="true" />}
            <span className="asCard__line">{card.line}</span>
          </p>
        </div>
        <a className="asCard__open" href={card.href} target={ASSIST_TAB} aria-label={`Open ${card.title}`} aria-describedby={lineId}>Open</a>
      </div>
    </div>
  );
}

/** One card's crash shows its title and a plain Open link. The fallback uses no hooks, no store, only strings. */
export class CardBoundary extends Component<{ href: string; title: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { /* contained: the transcript keeps working */ }
  render() {
    if (!this.state.failed) return this.props.children;
    const title = typeof this.props.title === "string" ? this.props.title : "";
    const href = typeof this.props.href === "string" && this.props.href.startsWith("/assist/") ? this.props.href : "/assist/";
    return (
      <div className="asCard asCard--plain">
        <a className="asCard__plain" href={href} target={ASSIST_TAB}>{title ? `Open ${title}` : "Open Assist"}</a>
      </div>
    );
  }
}

/** A crash of the list itself (outside any card) renders nothing: the transcript is never blanked. */
class Quiet extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { /* contained */ }
  render() { return this.state.failed ? null : this.props.children; }
}
